import React, { useState, useEffect } from "react";
import { IconX } from "@tabler/icons-react";
import { useCRM } from "../CRMContext";
import { createContact, updateContact, checkDuplicates } from "../services/contacts.service";
import NumberFormat from "react-number-format";
import { ColorPicker, ContactLabel } from "../Label";
import { getRandomColor } from "../label-utils";

const PRIORITIES = [
  { value: "", label: "Sem prioridade" },
  { value: "low", label: "Baixa" },
  { value: "medium", label: "Média" },
  { value: "high", label: "Alta" },
];

const TEMPERATURES = [
  { value: "", label: "Sem temperatura" },
  { value: "cold", label: "Frio" },
  { value: "warm", label: "Morno" },
  { value: "hot", label: "Quente" },
];

const formatPhoneForAPI = (phoneNumber) => {
  if (!phoneNumber) return "";
  const numbersOnly = phoneNumber.replace(/\D/g, "");
  return `${numbersOnly}`;
};

const ContactForm = ({ contact, columnId, onClose, onSaved }) => {
  const { state, showToast, addContactToColumn, updateContactInColumn } = useCRM();

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [position, setPosition] = useState("");
  const [dealValue, setDealValue] = useState("");
  const [priority, setPriority] = useState("");
  const [temperature, setTemperature] = useState("");
  const [owner, setOwner] = useState("");
  const [expectedCloseDate, setExpectedCloseDate] = useState("");
  const [observations, setObservations] = useState("");
  const [labelName, setLabelName] = useState("");
  const [labelColor, setLabelColor] = useState(getRandomColor());
  const [lossReason, setLossReason] = useState("");
  const [lossNotes, setLossNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (contact) {
      setName(contact.name || "");
      setPhone(contact.phone || "");
      setEmail(contact.email || "");
      setCompany(contact.company || "");
      setPosition(contact.position || "");
      setDealValue(contact.deal_value || "");
      setPriority(contact.priority || "");
      setTemperature(contact.temperature || "");
      setOwner(contact.owner || "");
      setExpectedCloseDate(contact.expected_close_date || "");
      setObservations(contact.observations || "");
      setLossReason(contact.loss_reason || "");
      setLossNotes(contact.loss_notes || "");
      if (contact.label) {
        setLabelName(contact.label.name || "");
        setLabelColor(contact.label.color || getRandomColor());
      }
    }
  }, [contact]);

  const handleDuplicateCheck = async (field, value) => {
    if (!value) return;
    try {
      const phoneVal = field === "phone" ? formatPhoneForAPI(value) : undefined;
      const emailVal = field === "email" ? value : undefined;
      const resp = await checkDuplicates(phoneVal, emailVal, contact?.id);
      setDuplicates(resp.duplicates || []);
    } catch {
      // silent
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!name.trim()) newErrors.name = "Nome é obrigatório";
    if (!phone) newErrors.phone = "Telefone é obrigatório";
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return; }

    setSubmitting(true);
    try {
      const data = {
        name: name.trim(),
        phone: formatPhoneForAPI(phone),
        email: email.trim() || undefined,
        company: company.trim() || undefined,
        position: position.trim() || undefined,
        deal_value: dealValue ? Number(dealValue) : undefined,
        priority: priority || undefined,
        temperature: temperature || undefined,
        owner: owner || undefined,
        expected_close_date: expectedCloseDate || undefined,
        observations: observations.trim() || undefined,
        loss_reason: lossReason.trim() || undefined,
        loss_notes: lossNotes.trim() || undefined,
        label: labelName.trim() ? { name: labelName.trim(), color: labelColor } : null,
      };

      if (contact) {
        const updated = await updateContact(contact.id, data);
        updateContactInColumn(updated.column, updated);
        showToast("Contato atualizado!");
        onSaved?.(updated);
      } else {
        data.column = columnId;
        data.pipeline = state.activePipelineId;
        const created = await createContact(data);
        addContactToColumn(columnId, created);
        showToast("Contato criado!");
        onSaved?.(created);
      }
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.error || "Erro ao salvar contato.";
      showToast(msg, "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="crm-modal-backdrop" onClick={onClose}>
      <div className="crm-modal crm-modal--lg" onClick={(e) => e.stopPropagation()}>
        <div className="crm-modal__header">
          <h3 className="crm-modal__title">{contact ? "Editar Contato" : "Novo Contato"}</h3>
          <button className="crm-drawer__close" onClick={onClose}><IconX size={20} /></button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="crm-modal__body">
            {duplicates.length > 0 && (
              <div style={{ padding: "10px 14px", background: "#fffbeb", border: "1px solid #fef3c7", borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
                Possíveis duplicatas encontradas: {duplicates.map((d) => d.name || d.phone).join(", ")}
              </div>
            )}

            <div className="crm-form-row">
              <div className="crm-form-group">
                <label className="crm-label">Nome *</label>
                <input className={`crm-input ${errors.name ? "crm-input--error" : ""}`} value={name} onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: "" })); }} />
                {errors.name && <div className="crm-error-text">{errors.name}</div>}
              </div>
              <div className="crm-form-group">
                <label className="crm-label">Telefone *</label>
                <NumberFormat
                  format="(##) #####-####"
                  className={`crm-input ${errors.phone ? "crm-input--error" : ""}`}
                  placeholder="(00) 00000-0000"
                  value={phone}
                  onValueChange={(v) => { setPhone(v.value); setErrors((p) => ({ ...p, phone: "" })); }}
                  onBlur={() => handleDuplicateCheck("phone", phone)}
                />
                {errors.phone && <div className="crm-error-text">{errors.phone}</div>}
              </div>
            </div>

            <div className="crm-form-row">
              <div className="crm-form-group">
                <label className="crm-label">E-mail</label>
                <input type="email" className="crm-input" value={email} onChange={(e) => setEmail(e.target.value)} onBlur={() => handleDuplicateCheck("email", email)} placeholder="email@exemplo.com" />
              </div>
              <div className="crm-form-group">
                <label className="crm-label">Empresa</label>
                <input className="crm-input" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Nome da empresa" />
              </div>
            </div>

            <div className="crm-form-row">
              <div className="crm-form-group">
                <label className="crm-label">Cargo</label>
                <input className="crm-input" value={position} onChange={(e) => setPosition(e.target.value)} placeholder="Cargo/Função" />
              </div>
              <div className="crm-form-group">
                <label className="crm-label">Valor do Negócio</label>
                <input type="number" className="crm-input" value={dealValue} onChange={(e) => setDealValue(e.target.value)} placeholder="0.00" step="0.01" />
              </div>
            </div>

            <div className="crm-form-row">
              <div className="crm-form-group">
                <label className="crm-label">Prioridade</label>
                <select className="crm-select" value={priority} onChange={(e) => setPriority(e.target.value)}>
                  {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div className="crm-form-group">
                <label className="crm-label">Temperatura</label>
                <select className="crm-select" value={temperature} onChange={(e) => setTemperature(e.target.value)}>
                  {TEMPERATURES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>

            <div className="crm-form-row">
              <div className="crm-form-group">
                <label className="crm-label">Responsável</label>
                <select className="crm-select" value={owner} onChange={(e) => setOwner(e.target.value)}>
                  <option value="">Selecionar...</option>
                  {state.teamMembers.map((m) => <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>)}
                </select>
              </div>
              <div className="crm-form-group">
                <label className="crm-label">Previsão de Fechamento</label>
                <input type="date" className="crm-input" value={expectedCloseDate} onChange={(e) => setExpectedCloseDate(e.target.value)} />
              </div>
            </div>

            <div className="crm-form-group">
              <label className="crm-label">Observações</label>
              <textarea className="crm-textarea" rows={3} value={observations} onChange={(e) => setObservations(e.target.value)} placeholder="Anotações sobre o contato..." />
            </div>

            {/* Label */}
            <div className="crm-form-group">
              <label className="crm-label">Etiqueta</label>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input className="crm-input" style={{ maxWidth: 200 }} placeholder="Nome da etiqueta" value={labelName} onChange={(e) => setLabelName(e.target.value)} />
                {labelName && (
                  <button type="button" className="crm-btn crm-btn--sm crm-btn--danger" onClick={() => setLabelName("")}>Remover</button>
                )}
              </div>
              {labelName && (
                <div style={{ marginTop: 8 }}>
                  <ColorPicker selectedColor={labelColor} onChange={setLabelColor} />
                  <div style={{ marginTop: 8 }}>
                    <ContactLabel label={{ name: labelName, color: labelColor }} />
                  </div>
                </div>
              )}
            </div>

            {/* Loss fields (visible when editing) */}
            {contact && (
              <>
                <div className="crm-divider" />
                <div className="crm-form-row">
                  <div className="crm-form-group">
                    <label className="crm-label">Motivo da Perda</label>
                    <input className="crm-input" value={lossReason} onChange={(e) => setLossReason(e.target.value)} placeholder="Ex: Preço, Concorrência" />
                  </div>
                  <div className="crm-form-group">
                    <label className="crm-label">Notas da Perda</label>
                    <input className="crm-input" value={lossNotes} onChange={(e) => setLossNotes(e.target.value)} placeholder="Detalhes..." />
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="crm-modal__footer">
            <button type="button" className="crm-btn" onClick={onClose}>Cancelar</button>
            <button type="submit" className="crm-btn crm-btn--primary" disabled={submitting}>
              {submitting ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;
